import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * EditorSection component for the poetry editor
 *
 * This component provides a text editor with basic formatting controls
 * for users to write and edit their poetry.
 *
 * @param {Object} props
 * @param {string} props.content - The current content of the editor
 * @param {function} props.onContentChange - Function to update the editor content
 * @param {string} props.scenario - Current scenario
 * @param {string} props.pid - Participant ID
 * @param {number} props.sessionStartTime - Session start timestamp
 */

function computeLineEvents(oldText, newText) {
    const oldLines = (oldText || '').split('\n');
    const newLines = (newText || '').split('\n');

    // fast-skip common prefix/suffix to keep events small
    let start = 0;
    while (start < oldLines.length && start < newLines.length && oldLines[start] === newLines[start]) {
        start++;
    }
    let endOld = oldLines.length - 1;
    let endNew = newLines.length - 1;
    while (endOld >= start && endNew >= start && oldLines[endOld] === newLines[endNew]) {
        endOld--; endNew--;
    }

    const events = [];

    // deleted-only region
    for (let i = start; i <= endOld; i++) {
        events.push({ type: 'line_delete', line_index: i, text: oldLines[i] });
    }

    // added / edited region
    const maxMid = Math.max(0, endNew - start + 1, endOld - start + 1);
    for (let k = 0; k < maxMid; k++) {
        const iOld = start + k;
        const iNew = start + k;
        const oldExists = iOld <= endOld;
        const newExists = iNew <= endNew;

        if (oldExists && newExists) {
            const oldLine = oldLines[iOld] ?? '';
            const newLine = newLines[iNew] ?? '';
            if (oldLine !== newLine) {
                // word-level slice diff (simple + cheap)
                const ow = oldLine.trim().split(/\s+/).filter(Boolean);
                const nw = newLine.trim().split(/\s+/).filter(Boolean);
                let p = 0;
                while (p < ow.length && p < nw.length && ow[p] === nw[p]) p++;
                let s = 0;
                while (s < ow.length - p && s < nw.length - p && ow[ow.length - 1 - s] === nw[nw.length - 1 - s]) s++;

                const fromWords = ow.slice(p, ow.length - s).join(' ');
                const toWords   = nw.slice(p, nw.length - s).join(' ');

                events.push({
                    type: 'word_change',
                    line_index: iNew,
                    from: fromWords,
                    to: toWords,
                    old_line: oldLine,
                    new_line: newLine
                });
            }
        } else if (!oldExists && newExists) {
            // pure addition
            events.push({ type: 'line_add', line_index: iNew, text: newLines[iNew] });
        }
    }

    return events;
}

function formatLineEvents(events) {
    // keep backend happy by sending a compact string (no schema change needed)
    return events.map(e => {
        const L = `L${e.line_index + 1}`;
        if (e.type === 'line_add')    return `[${L}] add: "${e.text}"`;
        if (e.type === 'line_delete') return `[${L}] delete: "${e.text}"`;
        // word_change
        return `[${L}] word_change: "${e.from}" → "${e.to}"`;
    }).join(' | ');
}

const logEditorInteraction = async (pid, scenario, actionType, editorContent, contentDiff, additionalData) => {
    try {
        await fetch('/api/log/editor', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                scenario,
                action_type: actionType,
                editor_content: editorContent,
                content_diff: contentDiff,
                additional_data: additionalData
            })
        });
    } catch (error) {
        console.error('Error logging editor interaction:', error);
    }
};

const EditorSection = ({ content, onContentChange, scenario, pid, sessionStartTime, pausedTotalMs = 0 }) => {
    // State for font size
    const [fontSize, setFontSize] = useState(16);

    // History for undo/redo functionality
    const [history, setHistory] = useState([content]);
    const [historyIndex, setHistoryIndex] = useState(0);

    // for track and log the changes of content
    const prevContentRef = useRef(content);

    // Navigation & location
    const navigate = useNavigate();
    const { search } = useLocation();

    // Extract scenario from query string
    const params = new URLSearchParams(search);

    // const scenario = params.get('scenario') || 'curator';

    const lastLoggedContentRef = useRef(content);   // last content we actually logged
    const logTimerRef = useRef(null);
    const latestContentRef = useRef(content);


    useEffect(() => {
        return () => { if (logTimerRef.current) clearTimeout(logTimerRef.current); };
    }, []);

    // Handle undo action
    const handleUndo = () => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            onContentChange(history[newIndex]);

            logEditorInteraction(
                pid,
                scenario,
                'undo',
                history[newIndex],
                null,
                { historyIndex: newIndex }
            );
        }
    };

    // Handle redo action
    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            onContentChange(history[newIndex]);

            logEditorInteraction(
                pid,
                scenario,
                'redo',
                history[newIndex],
                null,
                { historyIndex: newIndex }
            );
        }
    };

    // Handle content change with history tracking
    const handleContentChange = (newContent) => {
        const oldContent = prevContentRef.current;
        onContentChange(newContent);

        // Add to history if content changed
        if (newContent !== history[historyIndex]) {
            const newHistory = history.slice(0, historyIndex + 1);
            newHistory.push(newContent);

            // Limit history size
            if (newHistory.length > 50) {
                newHistory.shift();
            }

            setHistory(newHistory);
            setHistoryIndex(newHistory.length - 1);
        }

        // track the latest editor content
        latestContentRef.current = newContent;

        // debounce logging so we don't spam every keystroke
        if (logTimerRef.current) clearTimeout(logTimerRef.current);
        logTimerRef.current = setTimeout(() => {
                const oldText = lastLoggedContentRef.current;
                const newText = latestContentRef.current;

                const events = computeLineEvents(oldText, newText);
                if (events.length) {
                    const diffString = formatLineEvents(events);
                    const stats = {
                        lines_total: newText.split('\n').length,
                        added: events.filter(e => e.type === 'line_add').length,
                        deleted: events.filter(e => e.type === 'line_delete').length,
                        word_changed: events.filter(e => e.type === 'word_change').length
                    };

                    logEditorInteraction(
                        pid,
                        scenario,
                        'content_change',   // keep same action_type for compatibility
                        newText,            // still send full content
                        diffString,         // compact per-line/per-word summary
                        stats               // small summary object
                    );

                    lastLoggedContentRef.current = newText;
                }
            }, 600);

        prevContentRef.current = newContent;
    };

    const flushPendingLog = () => {
        if (!logTimerRef.current) return;
        clearTimeout(logTimerRef.current);
        logTimerRef.current = null;

        const oldText = lastLoggedContentRef.current;
        const newText = latestContentRef.current;

        const events = computeLineEvents(oldText, newText);
        if (events.length) {
            const diffString = formatLineEvents(events);
            const stats = {
                lines_total: newText.split('\n').length,
                added: events.filter(e => e.type === 'line_add').length,
                deleted: events.filter(e => e.type === 'line_delete').length,
                word_changed: events.filter(e => e.type === 'word_change').length
            };
            logEditorInteraction(pid, scenario, 'content_change', newText, diffString, stats);
            lastLoggedContentRef.current = newText;
        }
    };


    // Font size controls
    const increaseFontSize = () => {
        // if (fontSize < 24) setFontSize(fontSize + 1);
        if (fontSize < 24) {
            const newSize = fontSize + 1;
            setFontSize(newSize);

            logEditorInteraction(
                pid,
                scenario,
                'font_size',
                content,
                null,
                { fontSize: newSize, action: 'increase' }
            );
        }
    };
    const decreaseFontSize = () => {
        // if (fontSize > 12) setFontSize(fontSize - 1);
        if (fontSize > 12) {
            const newSize = fontSize - 1;
            setFontSize(newSize);

            logEditorInteraction(
                pid,
                scenario,
                'font_size',
                content,
                null,
                { fontSize: newSize, action: 'decrease' }
            );
        }
    };

    const handlePublish = () => {
        flushPendingLog();
        const creationDuration = sessionStartTime
            ? Math.floor((Date.now() - sessionStartTime - (pausedTotalMs || 0)) / 1000)
            : null;

        logEditorInteraction(
            pid,
            scenario,
            'publish',
            content,
            null,
            { creationDuration, pausedTotalMs }     // include paused in the log meta
        );

        navigate(`/tag-poem${search}`, {
            state: {
                poemDraft: content,
                scenario,
                pid,
                creationDuration
            }
        });
    };
    return (
        <div className="draft-section">
            <h2 className="section-title">Draft Poem</h2>
            <div className="editor-container">
                <div className="editor-toolbar">
                    <button
                        className="toolbar-button"
                        onClick={handleUndo}
                        title="Undo"
                    >
                        ↩ Undo
                    </button>
                    <button
                        className="toolbar-button"
                        onClick={handleRedo}
                        title="Redo"
                    >
                        ↪ Redo
                    </button>
                    <button
                        className="toolbar-button"
                        onClick={decreaseFontSize}
                        title="Decrease font size"
                    >
                        A-
                    </button>
                    <button
                        className="toolbar-button"
                        onClick={increaseFontSize}
                        title="Increase font size"
                    >
                        A+
                    </button>
                </div>
                <textarea
                    className="editor-content"
                    value={content}
                    onChange={(e) =>
                        handleContentChange(e.target.value)
                    }
                    onBlur={flushPendingLog}
                    placeholder="Start writing your poem here..."
                    style={{ fontSize: `${fontSize}px` }}
                />
                <div className="editor-footer">
                    <button
                        className="publish-button"
                        onClick={handlePublish}
                        disabled={content.length === 0}
                    >
                        Publish
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditorSection;
